import { SparkleIcon } from "@/components/icons/Sparkle";
import { SearchInput } from "@/components/ui/input";
import { Skill } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { SkillPickerCategoryTabs } from "./SkillPickerCategoryTabs";
import { SkillPickerRow } from "./SkillPickerRow";
import { SkillsGroupedList } from "./SkillsGroupedList";
import {
  SkillPickerPools,
  SkillPickerSource,
  SkillPickerTab,
  buildSkillPickerGroups,
  buildSkillPickerTabs,
  skillRequiresProjectKitLink,
} from "./skills-picker-lib";

type SkillListItem = {
  skill: Skill;
  source: SkillPickerSource;
};

export const SkillsPickerPanel: React.FC<{
  pools: SkillPickerPools;
  showGroup: boolean;
  onSelect: (input: {
    skillUuid: string;
    requiresKitLink: boolean;
  }) => void | Promise<void>;
}> = ({ pools, showGroup, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState<SkillPickerTab>("all");

  const tabs = useMemo(
    () => buildSkillPickerTabs(pools, searchQuery),
    [pools, searchQuery],
  );

  const groups = useMemo(
    () => buildSkillPickerGroups(selectedTab, pools, searchQuery, showGroup),
    [selectedTab, pools, searchQuery, showGroup],
  );

  const hasResults = groups.some((group) => group.skills.length > 0);

  const listGroups = groups.map((group) => ({
    title: group.title,
    items: group.skills.map((skill) => ({
      skill,
      source: group.source,
    })),
  }));

  return (
    <div className="flex max-w-[400px] flex-col gap-2">
      <h2 className="font-family-fancy flex flex-row items-center gap-2 text-lg font-bold">
        <SparkleIcon size={18} />
        All Skills
      </h2>
      <SearchInput
        placeholder="Search skills..."
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.target.value);
          setSelectedTab("all");
        }}
        className="text-xs"
      />
      {hasResults ? (
        <>
          <SkillPickerCategoryTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
          />
          <div className="max-h-[300px] overflow-y-auto">
            <SkillsGroupedList<SkillListItem>
              groups={listGroups}
              getItemKey={(item) => item.skill.uuid}
              renderItem={(item) => <SkillPickerRow skill={item.skill} />}
              onSelectItem={(item) =>
                onSelect({
                  skillUuid: item.skill.uuid,
                  requiresKitLink: skillRequiresProjectKitLink(item.source),
                })
              }
            />
          </div>
        </>
      ) : (
        <div className="font-family-fancy px-2 py-1 text-sm text-gray-500">
          <FontAwesomeIcon icon={faBan} /> No results
        </div>
      )}
    </div>
  );
};
